import { For, Show } from 'solid-js';
import { AirtableLink } from '~shared/ui/AirtableLink';
import { CARD_CLASSES } from '~shared/ui/Card';
import { Chip } from '~shared/ui/Chip';
import { classNames } from '~shared/lib/classes';
import { clinicUrl, memberUrl, teamUrl } from '~/stores/snapshotStore';
import type { MemberHistory } from '~/lib/types';

type Props = {
  history: MemberHistory;
  duplicateOfName: string | null;
};

export function MemberCard(props: Props) {
  const m = () => props.history.member;
  const hasNothing = () =>
    props.history.teams.length === 0 && props.history.clinics.length === 0;

  return (
    <div class={classNames(CARD_CLASSES, 'flex flex-col gap-3')}>
      <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <AirtableLink href={memberUrl(m().id)} class="text-base font-semibold">
          {m().name || '(unnamed record)'}
        </AirtableLink>
        <Show when={m().email}>
          <span class="text-muted font-mono text-xs">{m().email}</span>
        </Show>
        <Show when={m().phone}>
          <span class="text-muted font-mono text-xs">{m().phone}</span>
        </Show>
      </div>

      <div class="flex flex-wrap gap-1.5">
        <Show when={m().bobaMember}>
          <Chip tone="tier-1">BOBA member</Chip>
        </Show>
        <Show when={m().ftloMember}>
          <Chip tone="tier-2">FTLO member</Chip>
        </Show>
        <Show when={props.duplicateOfName}>
          {(n) => <Chip tone="warn">Duplicate of {n()}</Chip>}
        </Show>
        <Show when={hasNothing()}>
          <Chip tone="warn">No team or clinic records linked</Chip>
        </Show>
      </div>

      <div class="grid gap-4 sm:grid-cols-2">
        <section class="flex flex-col gap-1.5" aria-label="BOBA teams">
          <h4 class="text-muted text-xs font-semibold tracking-wide uppercase">
            BOBA teams ({props.history.teams.length})
          </h4>
          <Show
            when={props.history.teams.length}
            fallback={<p class="text-muted text-xs">No team records linked.</p>}
          >
            <ul class="flex flex-col gap-1">
              <For each={props.history.teams}>
                {(t) => (
                  <li class="text-sm">
                    <span class="text-muted">{t.seasonName}</span>{' '}
                    <AirtableLink href={teamUrl(t.teamId)} class="font-medium">
                      {t.teamName}
                    </AirtableLink>
                    <Show when={t.flavour}>
                      <span class="text-muted"> · {t.flavour}</span>
                    </Show>
                    <Show when={t.role !== 'Player'}>
                      <span class="text-tea-700"> · {t.role}</span>
                    </Show>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </section>

        <section class="flex flex-col gap-1.5" aria-label="FTLO clinics">
          <h4 class="text-muted text-xs font-semibold tracking-wide uppercase">
            FTLO clinics ({props.history.clinics.length})
          </h4>
          <Show
            when={props.history.clinics.length}
            fallback={
              <p class="text-muted text-xs">No clinic records linked.</p>
            }
          >
            <ul class="flex flex-col gap-1">
              <For each={props.history.clinics}>
                {(c) => (
                  <li class="text-sm">
                    <span class="text-muted">{c.termName}</span>{' '}
                    <AirtableLink
                      href={clinicUrl(c.clinicId)}
                      class="font-medium"
                    >
                      {c.clinicName}
                    </AirtableLink>
                    <Show when={c.level}>
                      <span class="text-muted"> · {c.level}</span>
                    </Show>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </section>
      </div>
    </div>
  );
}
